import i18n from "../i18n";
import moment from "moment";
import {
  Text,
  Group,
  Badge,
  Card,
  Loader,
  Tabs,
  ActionIcon,
  Table,
  Pagination,
  ScrollArea,
  Box,
  TextInput,
  Button,
  Alert,
} from "@mantine/core";
import DataTable from "./dataTable";
import { useEffect, useState, useMemo, Fragment, useRef, useCallback } from "react";
import { useDebouncedState } from "@mantine/hooks";
import { useServerApi } from "../hooks/userServerApi";
import { commissionCalendarDay } from "../helpers/commissionCalendarDate";
import { useCellRender } from "../hooks/useCellRender";
import { IconChevronDown, IconChevronRight } from "@tabler/icons";
import DiscountRateInfo from "./discountRateInfo";
import RemoteSelect2 from "./remoteSelect2";
import { formatConditionForDisplay } from "../helpers/commissionCustomConditionUtils";
import {
  consultantVisibleRate,
  getYearColumnDefs,
  grossRateFromScheduleRow,
  rowHasCoverRate,
  scheduleHasCoverRate,
} from "../helpers/commissionScheduleYears";

const PAGE_SIZE = 20;

const getProductCode = (row) =>
  row?.planCode || row?.product?.code || "---";

const formatRate = (rate, rateFactor) =>
  ((rate ?? 0) * 100 * rateFactor).toFixed(4);

const getRateColor = (rate, isSelected) => {
  if (isSelected) return "green";
  if (rate && rate > 0) return "teal";
  return undefined;
};

const createConditionColumn = ({ rowSelected, multiline = false }) => ({
  field: "condition",
  headerName: "Condition",
  cellRender: (col, data) => {
    if (data?._rateKind === "cover") {
      return (
        <Group spacing={4} noWrap>
          <Text size="xs" weight={600} color="blue">
            {i18n.t("Discount Rate")}
          </Text>
          <DiscountRateInfo />
        </Group>
      );
    }
    if (multiline) {
      return (
        <Text size="xs">
          {data?.conditions?.map(formatConditionForDisplay).join("\n")}
        </Text>
      );
    }
    return data?.conditions?.map((condition, idx) => (
      <Text
        key={idx}
        size="xs"
        color={rowSelected === data?._id ? "green" : undefined}
      >
        {formatConditionForDisplay(condition)}
      </Text>
    ));
  },
});

const createYearRateColumns = (
  rateKey,
  { rateFactor = 1, rowSelected = null, highlightSelected = true }
) => {
  const years = getYearColumnDefs();

  return years.map(({ key, label }) => ({
    field: `${rateKey}.${key}`,
    headerName: label,
    cellRender: (col, data) => {
      const rateVal = data?.[rateKey]?.[key];
      const isDiscount = data?._rateKind === "cover";
      const isSelected =
        !isDiscount &&
        highlightSelected &&
        rowSelected != null &&
        rowSelected === data?._id;
      const hasValue = Number(rateVal) > 0;
      return (
        <Text
          size="xs"
          color={isDiscount ? (hasValue ? "blue" : undefined) : getRateColor(rateVal, isSelected)}
          style={isDiscount && !hasValue ? { color: "#000" } : undefined}
        >
          {formatRate(rateVal, rateFactor)}%
        </Text>
      );
    },
  }));
};

const buildRateColumns = ({
  rateKey,
  rateFactor = 1,
  rowSelected = null,
  showConditionColumn = true,
  multilineCondition = false,
  highlightSelected = true,
}) => {
  const cols = showConditionColumn
    ? [createConditionColumn({ rowSelected, multiline: multilineCondition })]
    : [];
  return [
    ...cols,
    ...createYearRateColumns(rateKey, {
      rateFactor,
      rowSelected,
      highlightSelected,
    }),
  ];
};

export const CommissionScheduleProductCard = ({
  scheduleProductId,
  scheduleProduct: scheduleProductProp = null,
  version,
  name,
  showCode = true,
  showProductInfo = true,
  showGrossRateOnly = true,
  useCoverAsGross = false,
  rateFactor = 1,
  showConditionColumn = true,
  rowSelected = null,
  selectedProvider = null,
  embedded = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [api] = useServerApi();
  const [scheduleProduct, setScheduleProduct] = useState(scheduleProductProp);
  const [cellRender] = useCellRender();

  const grossRateColumns = useMemo(
    () =>
      buildRateColumns({
        rateKey: "grossRate",
        rateFactor,
        rowSelected,
        showConditionColumn,
        highlightSelected: true,
      }),
    [rateFactor, rowSelected, showConditionColumn]
  );

  const totalRateColumns = useMemo(
    () =>
      buildRateColumns({
        rateKey: "totalRate",
        rateFactor,
        showConditionColumn,
        multilineCondition: true,
        highlightSelected: false,
      }),
    [rateFactor, showConditionColumn]
  );

  const basicRateColumns = useMemo(
    () =>
      buildRateColumns({
        rateKey: "basicRate",
        rateFactor,
        showConditionColumn,
        multilineCondition: true,
        highlightSelected: false,
      }),
    [rateFactor, showConditionColumn]
  );

  const overrideRateColumns = useMemo(
    () =>
      buildRateColumns({
        rateKey: "overrideRate",
        rateFactor,
        showConditionColumn,
        multilineCondition: true,
        highlightSelected: false,
      }),
    [rateFactor, showConditionColumn]
  );

  const extraRateColumns = useMemo(
    () =>
      buildRateColumns({
        rateKey: "extraRate",
        rateFactor,
        showConditionColumn,
        multilineCondition: true,
        highlightSelected: false,
      }),
    [rateFactor, showConditionColumn]
  );

  const grossRows = useMemo(() => {
    const stacked = [];
    (scheduleProduct?.commissionTable || []).forEach((row, index) => {
      stacked.push({
        ...row,
        _rateKind: "gross",
        grossRate: grossRateFromScheduleRow(row),
      });
      if (!useCoverAsGross && rowHasCoverRate(row)) {
        stacked.push({
          ...row,
          _id: `${row._id || index}-cover`,
          _rateKind: "cover",
          grossRate: row.coverRate,
        });
      }
    });
    return stacked;
  }, [scheduleProduct, useCoverAsGross]);

  const consultantRows = useMemo(
    () =>
      (scheduleProduct?.commissionTable || []).map((row) => ({
        ...row,
        grossRate: consultantVisibleRate(row),
      })),
    [scheduleProduct]
  );

  const fetchScheduleProduct = async () => {
    try {
      if (!scheduleProductId) return;
      setLoading(true);
      const product = await api.getById({
        apiEntity: "commissionScheduleProduct",
        id: scheduleProductId,
      });
      setScheduleProduct(product);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (scheduleProductProp) {
      setScheduleProduct(scheduleProductProp);
    }
  }, [scheduleProductProp]);

  useEffect(() => {
    if (scheduleProductProp) return;
    fetchScheduleProduct();
  }, [scheduleProductId, scheduleProductProp]);

  const showCard =
    !selectedProvider ||
    selectedProvider === scheduleProduct?.provider?._id ||
    selectedProvider === scheduleProduct?.provider;

  if (!showCard) return null;

  const cardBody = loading ? (
    <Loader size="xs" />
  ) : (
    <>
      {!(embedded && !showCode && !showProductInfo) && (
        <Group position="apart" mb="md" align="flex-start">
          {(showCode || showProductInfo) && (
            <span>
              {showCode && scheduleProduct?.product?.code && (
                <Badge color="blue" size="xs" mb="md">
                  {scheduleProduct.product.code}
                </Badge>
              )}
              {showProductInfo &&
                cellRender.ProductInfo(scheduleProduct?.product, 12)}
            </span>
          )}

          <Group spacing="xs" align="center">
            {version && (
              <Badge size="xs" radius={0}>
                {version}
              </Badge>
            )}
            {name && (
              <Badge size="xs" color="green" radius={0}>
                {name}
              </Badge>
            )}
            <Badge
              size="xs"
              color={scheduleProduct?.professionalInvestor ? "orange" : "gray"}
              radius={0}
            >
              {scheduleProduct?.professionalInvestor ? "PI" : "Non-PI"}
            </Badge>
            {!useCoverAsGross && scheduleHasCoverRate(scheduleProduct?.commissionTable) && (
              <Badge size="xs" color="blue" radius={0}>
                $C
              </Badge>
            )}
            <Text size="xs" color="dimmed" ml="xs">
              {scheduleProduct?.commissionSchedule?.code
                ? `${scheduleProduct.commissionSchedule.code} > ${scheduleProduct.code}`
                : scheduleProduct?.code}
            </Text>
            {(scheduleProduct?.effectiveDate || scheduleProduct?.endDate) && (
              <Text size="xs" color="dimmed">
                (
                {scheduleProduct?.effectiveDate
                  ? commissionCalendarDay(scheduleProduct.effectiveDate)
                  : "---"}
                {" ~ "}
                {scheduleProduct?.endDate
                  ? commissionCalendarDay(scheduleProduct.endDate)
                  : "---"}
                )
              </Text>
            )}
          </Group>
        </Group>
      )}

      {showGrossRateOnly && (
        <DataTable
          columns={grossRateColumns}
          data={useCoverAsGross ? consultantRows : grossRows}
          hidePagination={true}
          hideSearchBar={true}
        />
      )}

      {!showGrossRateOnly && (
        <Tabs defaultValue="grossRate">
          <Tabs.List>
            <Tabs.Tab value="grossRate">{i18n.t("Gross Rate")}</Tabs.Tab>
            <Tabs.Tab value="totalRate">{i18n.t("Total Rate")}</Tabs.Tab>
            <Tabs.Tab value="basicRate">{i18n.t("Basic Rate")}</Tabs.Tab>
            <Tabs.Tab value="overrideRate">{i18n.t("Override Rate")}</Tabs.Tab>
            <Tabs.Tab value="extraRate">{i18n.t("Extra Rate")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="grossRate" pt="xs">
            <DataTable
              columns={grossRateColumns}
              data={grossRows}
              hidePagination={true}
              hideSearchBar={true}
            />
          </Tabs.Panel>

          <Tabs.Panel value="totalRate" pt="xs">
            <DataTable
              columns={totalRateColumns}
              data={scheduleProduct?.commissionTable}
              hidePagination={true}
              hideSearchBar={true}
            />
          </Tabs.Panel>

          <Tabs.Panel value="basicRate" pt="xs">
            <DataTable
              columns={basicRateColumns}
              data={scheduleProduct?.commissionTable}
              hidePagination={true}
              hideSearchBar={true}
            />
          </Tabs.Panel>

          <Tabs.Panel value="overrideRate" pt="xs">
            <DataTable
              columns={overrideRateColumns}
              data={scheduleProduct?.commissionTable}
              hidePagination={true}
              hideSearchBar={true}
            />
          </Tabs.Panel>

          <Tabs.Panel value="extraRate" pt="xs">
            <DataTable
              columns={extraRateColumns}
              data={scheduleProduct?.commissionTable}
              hidePagination={true}
              hideSearchBar={true}
            />
          </Tabs.Panel>
        </Tabs>
      )}
    </>
  );

  if (embedded) {
    return <Box py="xs">{cardBody}</Box>;
  }

  return (
    <Card withBorder mb="md">
      {cardBody}
    </Card>
  );
};

const CommissionScheduleProductList = ({ form }) => {
  const {
    productList,
    fileImportDate,
    fileName,
    _id: id,
  } = form.values;

  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useDebouncedState("", 300);
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [missingCodeHint, setMissingCodeHint] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const fetchRequestId = useRef(0);
  const prevSearchQuery = useRef(searchQuery);
  const backfillRan = useRef(false);

  const refreshProductList = useCallback(() => {
    setPage(0);
    setRefreshToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!form) return;
    form.refreshProductList = refreshProductList;
    return () => {
      delete form.refreshProductList;
    };
  }, [form, refreshProductList]);

  useEffect(() => {
    setSearchQuery(searchInput);
  }, [searchInput, setSearchQuery]);

  useEffect(() => {
    if (!id || backfillRan.current) return;
    backfillRan.current = true;

    apiRef.current.CommissionSchedule.backfillScheduleProductCodes(id).then(
      (result) => {
        if (result?.updated > 0) {
          setRefreshToken((token) => token + 1);
        }
      }
    );
  }, [id]);

  useEffect(() => {
    if (!docs.length) {
      setMissingCodeHint(false);
      return;
    }
    const missingCount = docs.filter(
      (row) => getProductCode(row) === "---"
    ).length;
    setMissingCodeHint(missingCount > 0 && missingCount / docs.length >= 0.8);
  }, [docs]);

  useEffect(() => {
    if (!id) return;

    if (prevSearchQuery.current !== searchQuery) {
      prevSearchQuery.current = searchQuery;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }

    const requestId = ++fetchRequestId.current;
    let cancelled = false;

    setExpandedIds(new Set());
    setLoading(true);

    apiRef.current.CommissionSchedule.getScheduleProductList(id, {
      page,
      limit: PAGE_SIZE,
      provider: selectedProvider,
      search: searchQuery,
    })
      .then((result) => {
        if (cancelled || requestId !== fetchRequestId.current) return;
        setDocs(result?.docs ?? []);
        setTotal(result?.total ?? 0);
      })
      .catch(console.log)
      .finally(() => {
        if (!cancelled && requestId === fetchRequestId.current) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id, page, selectedProvider, searchQuery, refreshToken]);

  const handleProviderChange = (data) => {
    const nextProvider = data?._id ?? null;
    setSelectedProvider((prev) => {
      if (prev !== nextProvider) {
        setPage(0);
      }
      return nextProvider;
    });
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.currentTarget.value);
  };

  const toggleExpand = (rowId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedIds(new Set(docs.map((row) => row._id)));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  const getProviderLabel = (row) =>
    row.insurer ||
    row.provider?.shortName ||
    row.provider?.code ||
    row.provider?.name ||
    "---";

  return (
    <>
      <Group align="flex-end" spacing="md" grow>
        <RemoteSelect2
          apiEntity="provider"
          select="name cname code shortName _id"
          pageSize={200}
          label="Search By Insurer"
          size="xs"
          searchFields={["name", "code", "cname"]}
          valueField="_id"
          labelField="name"
          labelRender={(data) =>
            `[${data.code ?? ""}] ${data.name ?? ""} ${data.cname ?? ""}`
          }
          onDataChange={handleProviderChange}
          clearable={true}
          sort={{ by: "name", order: "asc" }}
        />
        <TextInput
          label="Search By Product Code / Name"
          placeholder="Product code or name"
          size="xs"
          value={searchInput}
          onChange={handleSearchChange}
        />
      </Group>
      {missingCodeHint && (
        <Alert color="yellow" mb="md" title="Product codes missing">
          This schedule was imported before plan codes were stored, or the Excel
          Plan Code column was empty. Re-upload the commission schedule Excel
          file in the Files section above to populate product codes.
        </Alert>
      )}
      <Group position="apart" mb="md" align="flex-start" mt="md">
        <Group>
          <Text size="xs">File Name: {fileName || "---"}</Text>
          <Text size="xs">
            Import Date:{" "}
            {fileImportDate
              ? moment(fileImportDate).format("YYYY-MM-DD")
              : "---"}
          </Text>
          <Text size="xs">
            Total Products: {total || productList?.length || 0}
          </Text>
        </Group>
      </Group>

      {loading && docs.length === 0 ? (
        <Loader size="sm" />
      ) : docs.length === 0 ? (
        <Text size="sm" color="dimmed">
          No commission schedule products found.
        </Text>
      ) : (
        <>
          <Group position="right" mb="xs">
            <Button.Group>
              <Button size="xs" variant="default" onClick={collapseAll}>
                Collapse All
              </Button>
              <Button size="xs" variant="default" onClick={expandAll}>
                Expand All
              </Button>
            </Button.Group>
          </Group>
          <ScrollArea>
            <Table striped highlightOnHover fontSize="xs" opacity={loading ? 0.6 : 1}>
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th>{i18n.t("Insurer")}</th>
                  <th>{i18n.t("Product Code")}</th>
                  <th>{i18n.t("Product Name")}</th>
                  <th>PI</th>
                  <th>{i18n.t("Conditions")}</th>
                  <th>{i18n.t("Code")}</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((row) => {
                  const isExpanded = expandedIds.has(row._id);
                  return (
                    <Fragment key={row._id}>
                      <tr
                        key={row._id}
                        onClick={() => toggleExpand(row._id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <ActionIcon size="xs" variant="subtle">
                            {isExpanded ? (
                              <IconChevronDown size={14} />
                            ) : (
                              <IconChevronRight size={14} />
                            )}
                          </ActionIcon>
                        </td>
                        <td>{getProviderLabel(row)}</td>
                        <td>{getProductCode(row)}</td>
                        <td>{row.productName || row.product?.name || "---"}</td>
                        <td>
                          <Badge
                            size="xs"
                            color={row.professionalInvestor ? "orange" : "gray"}
                          >
                            {row.professionalInvestor ? "PI" : "Non-PI"}
                          </Badge>
                        </td>
                        <td>{row.numOfConditions ?? row.commissionTable?.length ?? 0}</td>
                        <td>{row.code}</td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${row._id}-detail`}>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <CommissionScheduleProductCard
                              scheduleProduct={row}
                              showCode={false}
                              showProductInfo={false}
                              embedded={true}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </ScrollArea>

          {totalPages > 1 && (
            <Group position="center" mt="md">
              <Pagination
                page={page + 1}
                onChange={(p) => setPage(p - 1)}
                total={totalPages}
                size="sm"
                disabled={loading}
              />
              <Text size="xs" color="dimmed">
                {total} records · page {page + 1} of {totalPages}
              </Text>
            </Group>
          )}
        </>
      )}
    </>
  );
};

export default CommissionScheduleProductList;
